import sha1 from 'js-sha1';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { downloadItem } from 'reactApp/appHelpers/appHelpers';
import { isViewContentOnly } from 'reactApp/appHelpers/settingsHelpers';
import { getWeblinkFromPublicId, isArchive, isPreviewable } from 'reactApp/modules/file/utils';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { MobileViewerItem } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerItem/MobileViewerItem';
import { sendViewerDwh } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { ViewerArchiveConnected } from 'reactApp/ui/ReactViewer/ViewerArchive/ViewerArchive';
import { sendXray } from 'reactApp/utils/ga';
import { sendCurrTimeRadar, sendPerfMetrics } from 'reactApp/utils/helpers';

import styles from './PublicFile.css';

interface IProps {
    file: CloudItem;
    storage: EStorageType;
    isPhone: boolean;
}

export const PublicFile = memo<IProps>(({ file, storage, isPhone }) => {
    const isPreviewableFile = isPreviewable(file);

    const handleDownload = useCallback(() => {
        downloadItem({ itemOrId: file, fromViewer: true });
    }, [file]);

    const sendDwh = useCallback(
        (data) => {
            sendViewerDwh({
                id_media: sha1(file?.id || ''),
                source: `${isPhone ? 'touch-' : ''}${storage}`,
                is_stories: false,
                id_public: getWeblinkFromPublicId(file?.id) ?? 'None',
                ...data,
            });
        },
        [file, storage]
    );

    useEffect(() => {
        sendPerfMetrics(`${isPhone ? 'm' : 'd'}pubfile`);
        sendCurrTimeRadar(`${isPhone ? 'm' : 'd'}pubfile_mnt`);
    }, []);

    const content = useMemo(() => {
        if (isArchive(file) && isPreviewableFile && !isPhone) {
            return (
                <ViewerArchiveConnected
                    file={file}
                    storage={storage}
                    sendGa={(action, label) => sendXray(['indpnewv-public', action, label])}
                    isPhone={true}
                    embedded={true}
                />
            );
        }
        return (
            <Router>
                <MobileViewerItem
                    isPublic
                    selected
                    forceArchiveViewer
                    itemId={file.id}
                    isSingle={true}
                    handleDownload={handleDownload}
                    storage={storage}
                    isViewContentOnly={isViewContentOnly}
                    sendAnalytics={sendDwh}
                />
            </Router>
        );
    }, [handleDownload, file, isPreviewableFile]);

    return (
        <div className={styles.root}>
            <div className={styles.content}>{content}</div>
        </div>
    );
});

PublicFile.displayName = 'PublicFile';
